import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useConfirm } from "../../components/useConfirm";
import {
    Users,
    BookOpen,
    PlusCircle,
    Calendar,
    Clock,
    X,
    Edit,
    Trash2,
    UserPlus,
    ArrowLeft,
} from "lucide-react";
import { API_BASE_URL } from "../../config/apiConfig";


const GroupExamsPage = () => {
    const { id } = useParams(); // group_id from route
    const navigate = useNavigate();
    const [group, setGroup] = useState(null);
    const [exams, setExams] = useState([]);
    const [showExamModal, setShowExamModal] = useState(false);
    const [showMemberModal, setShowMemberModal] = useState(false);
    const [editingDescription, setEditingDescription] = useState(false);
    const [newDescription, setNewDescription] = useState("");
    const [users, setUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [userSubjects, setUserSubjects] = useState([]);
    const [newMemberId, setNewMemberId] = useState("");
    const [examData, setExamData] = useState({
        title: "",
        subject: "",
        startTime: "",
        duration: 105, // minutes
        availableDuration: 6, // hours the exam stays open
        description: "",
        numberOfQuestions: "", // added field
    });



    ;
    const [newMemberEmail, setNewMemberEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const token = localStorage.getItem("token");
    const { confirm, ConfirmDialog } = useConfirm();

    useEffect(() => {
        const fetchGroup = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/groups/single-group/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });
                const data = await res.json();
                setGroup(data);

                if (data.exams) setExams(data.exams);
            } catch (err) {
                console.error("Error fetching group:", err);
            }
        };
        if (token) {
            fetchGroup();
        }
    }, [id, token]);

    // Create exam
    const handleCreateExam = async () => {
        if (!examData.title || !examData.subject || !examData.startTime || !examData.duration || !examData.availableDuration) {
            toast.error("Please fill in all required fields");
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/group-exams/${group.group_id}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    title: examData.title,
                    subject: examData.subject,
                    startTime: new Date(examData.startTime).toISOString(),
                    duration: examData.duration,
                    description: examData.description,
                    availableDuration: examData.availableDuration,
                    numberOfQuestions: examData.numberOfQuestions, // ✅ added
                }),

            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || "Failed to create exam");
            }

            setExams((prevExams) => [...prevExams, data.exam]);

            // Reset form (don’t forget subject & startTime)
            setExamData({
                title: "",
                subject: "",
                startTime: "",
                duration: "120",
                description: "",
            });

            setShowExamModal(false);
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 3000);
        } catch (err) {
            console.error("Error creating exam", err);
            toast.error(err.message || "Failed to create exam");
        } finally {
            setIsLoading(false);
        }
    };


    const handleDeleteExam = async (examId) => {
        setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/group-exams/${examId}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || "Failed to delete exam");
            }

            // Remove deleted exam from state
            setExams((prevExams) => prevExams.filter((exam) => exam.exam_id !== examId));

            // ✅ Toast success
            toast.success("Exam deleted successfully");
        } catch (err) {
            console.error("Error deleting exam:", err);
            toast.error(err.message || "Failed to delete exam");
        } finally {
            setIsLoading(false);
        }
    };



    useEffect(() => {
        if (searchQuery.trim() === "") {
            setFilteredUsers([]);
        } else {
            const results = users.filter((user) =>
                `${user.firstName} ${user.lastName} ${user.email}`
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase())
            );
            setFilteredUsers(results);
        }
    }, [searchQuery, users]);


    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/users`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                if (res.ok) {
                    // filter out those already in the group
                    const groupMemberIds = group?.members?.map((m) => m.user_id) || [];
                    const availableUsers = data.users.filter(
                        (u) => !groupMemberIds.includes(u.user_id)
                    );
                    setUsers(availableUsers);
                } else {
                    console.error(data.message);
                }
            } catch (err) {
                console.error("Error fetching users:", err);
            }
        };

        if (token && group) {
            fetchUsers();
        }
    }, [token, group]);


    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/users/me`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                // console.log("Raw data:", data);

                if (res.ok) {
                    const parsedSubjects = typeof data.subjects === "string"
                        ? JSON.parse(data.subjects)
                        : Array.isArray(data.subjects)
                            ? data.subjects
                            : [];

                    setUserSubjects(parsedSubjects);
                    // console.log("Parsed Subjects:", parsedSubjects);
                } else {
                    console.error(data.message || "Failed to fetch user subjects");
                }
            } catch (err) {
                console.error("Error fetching user profile:", err);
            }
        };

        if (token) {
            fetchUserProfile();
        }
    }, [token]);

    useEffect(() => {
        const fetchUserExams = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/group-exams/user/group/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                const data = await res.json();
                console.log(data)
                if (res.ok) {
                    setExams(data.exams); // ✅ only exams for this group
                } else {
                    console.error(data.message || "Failed to fetch user exams");
                }
            } catch (err) {
                console.error("Error fetching user exams:", err);
            }
        };

        if (token && id) {
            fetchUserExams();
        }
    }, [id, token]);




    const handleUpdateDescription = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/groups/${id}/update-description`, {
                method: "PATCH",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ description: newDescription }),
            });

            const data = await res.json();

            if (res.ok) {
                setGroup((prev) => ({
                    ...prev,
                    description: data.group.description,
                }));
                setEditingDescription(false);
            } else {
                toast.error(data.message || "Failed to update description");
            }
        } catch (err) {
            console.error("Error updating description:", err);
        }
    };

    const closeMemberModal = () => {
        setShowMemberModal(false);
        setSearchQuery("");
        setFilteredUsers([]);
        setNewMemberId("");
    };


    // Add member
    const handleAddMember = async () => {
        if (!newMemberId) return;

        try {
            const res = await fetch(`${API_BASE_URL}/api/groups/${id}/add-user/${newMemberId}`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ role: "member" }),
            });

            const data = await res.json();

            if (res.ok) {
                // Find full user details from users array
                const addedUser = users.find((u) => u.user_id === newMemberId);

                setGroup((prev) => ({
                    ...prev,
                    members: [
                        ...prev.members,
                        {
                            user_id: newMemberId,
                            role: "member",
                            firstName: addedUser?.firstName || "",
                            lastName: addedUser?.lastName || "",
                            email: addedUser?.email || "",
                        },
                    ],
                }));

                setNewMemberId("");
                setSearchQuery(""); // clear search
                closeMemberModal();
            } else {
                toast.error(data.message || "Failed to add member");
            }
        } catch (err) {
            console.error("Error adding member:", err);
        }
    };


    // Remove member
    const handleRemoveMember = async (userId) => {
        const ok = await confirm({
            title: "Remove this member?",
            message: "They'll lose access to this group and its exams.",
            confirmLabel: "Remove",
            danger: true,
        });
        if (!ok) return;

        try {
            const res = await fetch(`${API_BASE_URL}/api/groups/${id}/remove-user/${userId}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });

            if (res.ok) {
                setGroup((prev) => ({
                    ...prev,
                    members: prev.members.filter((m) => m.user_id !== userId),
                }));
            } else {
                const data = await res.json();
                toast.error(data.message || "Failed to remove member");
            }
        } catch (err) {
            console.error("Error removing member:", err);
        }
    };

    // Add state at the top
    const [editingExam, setEditingExam] = useState(null);

    // Function to handle updating an exam
    const handleUpdateExam = async () => {
        if (!editingExam.title || !editingExam.subject || !editingExam.startTime || !editingExam.duration) {
            toast.error("Please fill in all required fields");
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/group-exams/${group.group_id}/${editingExam.exam_id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    title: editingExam.title,
                    subject: editingExam.subject,
                    startTime: new Date(editingExam.startTime).toISOString(),
                    duration: editingExam.duration,
                    description: editingExam.description,
                }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || "Failed to update exam");
            }

            // Update exams state
            setExams((prevExams) =>
                prevExams.map((ex) => (ex.exam_id === editingExam.exam_id ? data.exam : ex))
            );

            setEditingExam(null);
            setShowExamModal(false);
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 3000);
        } catch (err) {
            console.error("Error updating exam", err);
            toast.error(err.message || "Failed to update exam");
        } finally {
            setIsLoading(false);
        }
    };


    if (!group) {
        return <p className="p-6 text-gray-500">Loading group...</p>;
    }

    return (
        <div className="p-0">
            {ConfirmDialog}
            {/* Top Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 shadow-sm px-4 py-3 flex items-center justify-between z-40">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-1 text-gray-700 hover:text-gray-900 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span className="text-sm font-medium">Back</span>
                </button>
                <h1 className="text-lg font-bold text-gray-900 text-center flex-1">
                    {group.groupName}
                </h1>
                {/* Right side placeholder (future icons/actions) */}
                <div className="w-10"></div>
            </div>

            <div className="p-6 space-y-6">
                {/* Group Info */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <div className="flex justify-between items-start">
                        <div className="flex-1">
                            <h1 className="text-2xl font-bold text-gray-900 mb-2">{group.groupName}</h1>
                            {editingDescription ? (
                                <div className="space-y-3">
                                    <textarea
                                        value={newDescription}
                                        onChange={(e) => setNewDescription(e.target.value)}
                                        className="w-full border rounded-lg p-3 text-gray-700"
                                        rows="3"
                                    />
                                    <div className="flex gap-3">
                                        <button
                                            onClick={handleUpdateDescription}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg"
                                        >
                                            Save
                                        </button>
                                        <button
                                            onClick={() => {
                                                setEditingDescription(false);
                                                setNewDescription(group.description || "");
                                            }}
                                            className="px-4 py-2 border rounded-lg text-gray-600"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-gray-600">
                                    {group.description || "No description"}
                                </p>
                            )}
                        </div>

                        {group.role === "admin" && !editingDescription && (
                            <button
                                onClick={() => {
                                    setNewDescription(group.description || ""); // 🟢 preload description
                                    setEditingDescription(true);
                                }}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <Edit className="w-5 h-5 text-gray-600" />
                            </button>
                        )}

                    </div>
                </div>


                {/* Members */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-bold flex items-center gap-2">
                            <Users className="w-5 h-5 text-blue-600" /> Members
                        </h2>
                        {group.role === "admin" && (
                            <button
                                onClick={() => setShowMemberModal(true)}
                                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 transition-colors text-sm"
                            >
                                <UserPlus className="w-4 h-4" />
                                Add Member
                            </button>
                        )}
                    </div>
                    <ul className="space-y-2">
                        {group.members?.map((member) => (
                            <li
                                key={member.user_id}
                                className="p-3 bg-gray-50 rounded-lg flex justify-between items-center"
                            >
                                <div>
                                    <span className="font-semibold">
                                        {member.firstName} {member.lastName}
                                    </span>{" "}
                                    <span className="text-gray-600">({member.email})</span>
                                    <p className="text-sm text-gray-500">Role: {member.role}</p>
                                </div>
                                {group.role === "admin" && member.role !== "admin" && (
                                    <button
                                        onClick={() => handleRemoveMember(member.user_id)}
                                        className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                                    >
                                        <Trash2 className="w-4 h-4 text-red-600" />
                                    </button>
                                )}

                            </li>
                        ))}
                    </ul>
                </div>

                {/* Exams Section */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                            <Calendar className="w-5 h-5 text-purple-600" />
                            Exams for {group.groupName}
                        </h3>
                        {group.role === "admin" && (
                            <button
                                onClick={() => setShowExamModal(true)}
                                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-xl hover:bg-green-700 transition-colors text-sm"
                            >
                                <PlusCircle className="w-4 h-4" />
                                Add Exam
                            </button>
                        )}
                    </div>

                    {exams.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-600">No exams scheduled for this group yet</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {[...exams]
                                .sort((a, b) => new Date(b.startTime) - new Date(a.startTime)) // 🧭 Newest first
                                .map((exam) => {
                                    const start = new Date(exam.startTime);
                                    const end = new Date(start.getTime() + exam.duration * 60000); // ✅ duration in minutes
                                    const now = new Date();
                                    const isElapsed = now > end;

                                    const examDate = start.toLocaleDateString();
                                    const examTime = start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

                                    return (
                                        <div
                                            key={exam.exam_id}
                                            className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div
                                                    className={`w-12 h-12 rounded-xl flex items-center justify-center ${exam.status === "upcoming"
                                                        ? "bg-blue-100 text-blue-600"
                                                        : isElapsed
                                                            ? "bg-red-100 text-red-600"
                                                            : "bg-green-100 text-green-600"
                                                        }`}
                                                >
                                                    <BookOpen className="w-6 h-6" />
                                                </div>

                                                <div>
                                                    <h4 className="font-semibold text-gray-900">{exam.title}</h4>
                                                    <div className="flex items-center gap-4 text-sm text-gray-600">
                                                        {isElapsed ? (
                                                            <span className="flex items-center gap-1 text-red-600 font-medium">
                                                                <Clock className="w-4 h-4" />
                                                                Time Elapsed
                                                            </span>
                                                        ) : (
                                                            <>
                                                                <span className="flex items-center gap-1">
                                                                    <Calendar className="w-4 h-4" />
                                                                    {examDate}
                                                                </span>
                                                                <span className="flex items-center gap-1">
                                                                    <Clock className="w-4 h-4" />
                                                                    {examTime}
                                                                </span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                {group.role === "admin" && (
                                                    <div className="flex gap-1">
                                                        {/* Optional admin buttons here */}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>

                    )}
                </div>


                {/* Add Member Modal */}
                {showMemberModal && (
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                        <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-4">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold">Add Member</h2>
                                <button
                                    onClick={closeMemberModal}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="relative mb-4">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search by name or email"
                                    className="w-full px-4 py-3 border rounded-lg"
                                />

                                {filteredUsers.length > 0 && (
                                    <ul className="absolute z-10 bg-white border rounded-lg mt-1 w-full max-h-40 overflow-y-auto">
                                        {filteredUsers.map((user) => (
                                            <li
                                                key={user.user_id}
                                                onClick={() => {
                                                    setNewMemberId(user.user_id); // ✅ backend expects userId
                                                    setSearchQuery(`${user.firstName} ${user.lastName} (${user.email})`);
                                                    setFilteredUsers([]);
                                                }}
                                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                            >
                                                {user.firstName} {user.lastName} ({user.email})
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={closeMemberModal}
                                    className="px-6 py-3 text-gray-700 border rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddMember}
                                    className="px-6 py-3 bg-blue-600 text-white rounded-lg"
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {/* Exam Creation Modal */}
            {showExamModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white rounded-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="p-8">
                            <div className="flex items-center justify-between mb-6 sticky top-0 bg-white z-10 pb-2">
                                <h2 className="text-xl font-bold">Create Exam</h2>
                                <button
                                    onClick={() => setShowExamModal(false)}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="space-y-4">
                                {/* Exam Title */}
                                <input
                                    type="text"
                                    placeholder="Exam Title"
                                    value={editingExam ? editingExam.title : examData.title}
                                    onChange={(e) =>
                                        editingExam
                                            ? setEditingExam({ ...editingExam, title: e.target.value })
                                            : setExamData({ ...examData, title: e.target.value })
                                    }
                                    className="w-full px-4 py-3 border rounded-lg"
                                />

                                {/* Subject Selection */}
                                <select
                                    value={editingExam ? editingExam.subject : examData.subject || ""}
                                    onChange={(e) =>
                                        editingExam
                                            ? setEditingExam({ ...editingExam, subject: e.target.value })
                                            : setExamData({ ...examData, subject: e.target.value })
                                    }
                                    className="w-full px-4 py-3 border rounded-lg"
                                >
                                    <option value="">Select the subject for the exam</option>
                                    {userSubjects.map((subj, idx) => (
                                        <option key={idx} value={subj}>
                                            {subj}
                                        </option>
                                    ))}
                                </select>

                                {/* Start Time */}
                                <input
                                    type="datetime-local"
                                    value={editingExam ? editingExam.startTime : examData.startTime || ""}
                                    onChange={(e) =>
                                        editingExam
                                            ? setEditingExam({ ...editingExam, startTime: e.target.value })
                                            : setExamData({ ...examData, startTime: e.target.value })
                                    }
                                    className="w-full px-4 py-3 border rounded-lg"
                                />

                                {/* Available For (Exam Window) */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Available For (Hours)
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="48"
                                        value={editingExam ? editingExam.availableDuration || "" : examData.availableDuration || ""}
                                        onChange={(e) =>
                                            editingExam
                                                ? setEditingExam({
                                                    ...editingExam,
                                                    availableDuration: Math.min(48, parseInt(e.target.value) || 0),
                                                })
                                                : setExamData({
                                                    ...examData,
                                                    availableDuration: Math.min(48, parseInt(e.target.value) || 0),
                                                })
                                        }
                                        className="w-full px-4 py-3 border rounded-lg"
                                        placeholder="e.g. 6"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        How long the exam will remain open for students to start (e.g. 6 hours).
                                    </p>
                                </div>


                                {/* Duration (Hours + Minutes) */}
                                <div className="flex gap-2">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Hours</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={
                                                (() => {
                                                    const totalMinutes = editingExam ? editingExam.duration : examData.duration;
                                                    return totalMinutes ? Math.floor(totalMinutes / 60) : "";
                                                })()
                                            }
                                            onChange={(e) => {
                                                const minutes = editingExam ? editingExam.duration % 60 : examData.duration % 60;
                                                const totalMinutes = parseInt(e.target.value || 0) * 60 + minutes;
                                                editingExam
                                                    ? setEditingExam({ ...editingExam, duration: totalMinutes })
                                                    : setExamData({ ...examData, duration: totalMinutes });
                                            }}
                                            className="w-full px-4 py-3 border rounded-lg"
                                            placeholder="e.g. 1"
                                        />
                                    </div>

                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Minutes</label>
                                        <input
                                            type="number"
                                            min="0"
                                            max="59"
                                            value={
                                                (() => {
                                                    const totalMinutes = editingExam ? editingExam.duration : examData.duration;
                                                    return totalMinutes ? totalMinutes % 60 : "";
                                                })()
                                            }
                                            onChange={(e) => {
                                                const hours = Math.floor((editingExam ? editingExam.duration : examData.duration) / 60);
                                                const totalMinutes = hours * 60 + parseInt(e.target.value || 0);
                                                editingExam
                                                    ? setEditingExam({ ...editingExam, duration: totalMinutes })
                                                    : setExamData({ ...examData, duration: totalMinutes });
                                            }}
                                            className="w-full px-4 py-3 border rounded-lg"
                                            placeholder="e.g. 30"
                                        />
                                    </div>
                                </div>

                                {/* Number of Questions */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Number of Questions
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="70"
                                        value={editingExam ? editingExam.numberOfQuestions || "" : examData.numberOfQuestions || ""}
                                        onChange={(e) =>
                                            editingExam
                                                ? setEditingExam({
                                                    ...editingExam,
                                                    numberOfQuestions: Math.min(70, parseInt(e.target.value) || 0),
                                                })
                                                : setExamData({
                                                    ...examData,
                                                    numberOfQuestions: Math.min(70, parseInt(e.target.value) || 0),
                                                })
                                        }
                                        className="w-full px-4 py-3 border rounded-lg"
                                        placeholder="e.g. 20"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        You can set up to <span className="font-semibold text-gray-700">70 questions</span> per exam.
                                    </p>
                                </div>



                                {/* Description */}
                                <textarea
                                    placeholder="Description (optional)"
                                    value={editingExam ? editingExam.description : examData.description}
                                    onChange={(e) =>
                                        editingExam
                                            ? setEditingExam({ ...editingExam, description: e.target.value })
                                            : setExamData({ ...examData, description: e.target.value })
                                    }
                                    className="w-full px-4 py-3 border rounded-lg"
                                    rows="3"
                                />

                                {/* Members (auto add) */}
                                <div className="bg-gray-50 p-3 rounded-lg border">
                                    <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                        Members Assigned to Exam
                                    </h3>
                                    <ul className="max-h-32 overflow-y-auto text-sm text-gray-600">
                                        {group.members?.map((m) => (
                                            <li key={m.user_id} className="py-1">
                                                ✅ {m.firstName} {m.lastName} ({m.email})
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    onClick={() => setShowExamModal(false)}
                                    className="px-6 py-3 text-gray-700 border rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={editingExam ? handleUpdateExam : handleCreateExam}
                                    className="px-6 py-3 bg-green-600 text-white rounded-lg"
                                    disabled={isLoading}
                                >
                                    {isLoading ? "Processing..." : editingExam ? "Update Exam" : "Create Exam"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default GroupExamsPage;
